import { ActionType, createAction, createReducer } from 'typesafe-actions';

import { ICustomError } from 'types';
import { ACTIONS } from '../constants';

export enum SocketStatus {
  Connected = 'Connected',
  Reconnected = 'Reconnected',
  Reconnecting = 'Reconnecting',
  Error = 'Error',
}

interface IWebSocketState {
  webSocketConnection: boolean;
  isGameMachineConnected: boolean;
  isSocketDisconnected: boolean;
  tableOpened: boolean;
  isTableTaken: boolean;
  socketStatusHistory: SocketStatus[];
  socketErrorHistory: string[];
}

export const connectToWebSocketAction = createAction(ACTIONS.CONNECT_TO_WEBSOCKET)<{
  tableId: string;
  token: string;
  gameType: number;
}>();

export const connectToChatWebSocketAction = createAction(ACTIONS.CONNECT_TO_CHAT_WEBSOCKET)<{
  token?: string | null;
}>();

export const connectToWebSocketSuccessAction = createAction(ACTIONS.CONNECT_TO_WEBSOCKET_SUCCESS)();
export const connectToChatWebSocketSuccessAction = createAction(
  ACTIONS.CONNECT_TO_CHAT_WEBSOCKET_SUCCESS,
)();
export const connectToWebSocketFailureAction = createAction(
  ACTIONS.CONNECT_TO_WEBSOCKET_FAILURE,
)<ICustomError>();
export const connectToChatWebSocketFailureAction = createAction(
  ACTIONS.CONNECT_TO_CHAT_WEBSOCKET_FAILURE,
)<ICustomError>();

export const disconnectFromWebSocketAction = createAction(ACTIONS.DISCONNECT_FROM_WEBSOCKET)();
export const clearSagaChannels = createAction(ACTIONS.CLEAR_SAGA_CHANNELS)();

export const openTableAction = createAction(ACTIONS.OPEN_TABLE)<{ id: string }>();

export const openTableFailureAction = createAction(ACTIONS.OPEN_TABLE_FAILURE)<{
  status: number;
  message: string;
}>();
export const openTableSuccessAction = createAction(ACTIONS.OPEN_TABLE_SUCCESS)();

export const tableClosedAction = createAction(ACTIONS.TABLE_CLOSED)();
export const tableClosedSuccessAction = createAction(ACTIONS.TABLE_CLOSED_SUCCESS)();

export const gameMachineConnectedAction = createAction(ACTIONS.GAME_MACHINE_CONNECTED)();
export const gameMachineDisconnectedAction = createAction(ACTIONS.GAME_MACHINE_DISCONNECTED)();

export const socketConnectedAction = createAction(ACTIONS.SOCKET_CONNECTED)();
export const socketDisconnectedAction = createAction(ACTIONS.SOCKET_DISCONNECTED)();

export const tableTakenAction = createAction(ACTIONS.TABLE_TAKEN)();
export const tableNoTakenAction = createAction(ACTIONS.TABLE_NO_TAKEN)();

export const closeTableAction = createAction(ACTIONS.CLOSE_TABLE_ACTION)();

export const setSocketStateHistory = createAction(ACTIONS.SET_SOCKET_STATE_HISTORY)<{
  status: SocketStatus;
  error: string;
}>();

export const defaultState: IWebSocketState = {
  webSocketConnection: false,
  isGameMachineConnected: false,
  isSocketDisconnected: false,
  tableOpened: false,
  isTableTaken: false,
  socketStatusHistory: [],
  socketErrorHistory: [],
};

const actions = {
  connectToWebSocketSuccessAction,
  disconnectFromWebSocketAction,
  openTableSuccessAction,
  tableClosedSuccessAction,
  gameMachineConnectedAction,
  gameMachineDisconnectedAction,
  socketConnectedAction,
  socketDisconnectedAction,
  tableTakenAction,
  tableNoTakenAction,
  closeTableAction,
  setSocketStateHistory,
};

export const websocketReducer = createReducer<IWebSocketState, ActionType<typeof actions>>(
  defaultState,
)
  .handleAction(connectToWebSocketSuccessAction, (state) => ({
    ...state,
    webSocketConnection: true,
  }))
  .handleAction(disconnectFromWebSocketAction, (state) => ({
    ...state,
    webSocketConnection: false,
  }))
  .handleAction(gameMachineConnectedAction, (state) => ({
    ...state,
    isGameMachineConnected: true,
  }))
  .handleAction(gameMachineDisconnectedAction, (state) => ({
    ...state,
    isGameMachineConnected: false,
  }))
  .handleAction(socketConnectedAction, (state) => ({
    ...state,
    isSocketDisconnected: false,
  }))
  .handleAction(socketDisconnectedAction, (state) => ({
    ...state,
    isSocketDisconnected: true,
  }))
  .handleAction(openTableSuccessAction, (state) => ({
    ...state,
    tableOpened: true,
  }))
  .handleAction(tableClosedSuccessAction, (state) => ({
    ...state,
    tableOpened: false,
  }))
  .handleAction(tableTakenAction, (state) => ({
    ...state,
    isTableTaken: true,
  }))
  .handleAction(tableNoTakenAction, (state) => ({
    ...state,
    isTableTaken: false,
  }))
  .handleAction(closeTableAction, (state) => ({
    ...state,
    tableOpened: false,
  }))
  .handleAction(setSocketStateHistory, (state, { payload }) => {
    if (payload.status === SocketStatus.Connected) {
      return {
        ...state,
        socketStatusHistory: [],
        socketErrorHistory: [],
      };
    }
    return {
      ...state,
      socketStatusHistory: [...state.socketStatusHistory.slice(-9), payload.status],
      socketErrorHistory: [...state.socketErrorHistory.slice(-9), payload.error],
    };
  });
